import { useState, useEffect, useCallback } from "react";

// Components
import Table from "./Childs/Table";
import Delete from "./Delete";
import Form from "./Childs/Form";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import FormCard from "../../Common/Childs/Structure/FormCard";

// Context
import { useGlobal } from "../../../context/GlobalContextProvider";

// Assets
import plus from "../../../assets/icons/plus-solid.svg";

// Constants
const INITIAL_FORM_STATE = {
  id: 0,
  rank: 0,
  color_hex_code: "#000000",
};

const DESCRIPTION_TEXT = 
  "Set the background colors and patterns that your Fund Raisin Art Pack will use, and the NFT price rankings for these colors.";

// API Service
const backgroundAPI = {
  getAll: async (artPackId) => {
    const res = await fetch(`/api/artpack/${artPackId}/background`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },

  create: async (artPackId, data) => {
    return fetch(`/api/artpack/${artPackId}/background`, {
      method: "POST",
      body: JSON.stringify({
        rank: data.rank,
        art_pack_id: artPackId,
        color_hex_code: data.color_hex_code,
      }),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  update: async (artPackId, id, data) => {
    return fetch(`/api/artpack/${artPackId}/background/${id}`, {
      method: "PUT",
      body: JSON.stringify({
        rank: data.rank,
        art_pack_id: artPackId,
        color_hex_code: data.color_hex_code,
      }),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  delete: async (artPackId, id) => {
    return fetch(`/api/artpack/${artPackId}/background/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },
};


const Index = () => {
  const { artPack, setSpinner } = useGlobal();

  const [backgrounds, setBackgrounds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  // Normalize background data
  const normalizeData = useCallback((items) => {
    return items.map((el) => ({
      background_id: el.background_id,
      rank: el.rank,
      art_pack_id: el.art_pack_id,
      color_hex_code: el.color_hex_code,
    }));
  }, []);

  // Load backgrounds from API
  const loadBackgrounds = useCallback(async () => {
    if (!artPack?.art_pack_id) return;

    setSpinner(true);
    try {
      const data = await backgroundAPI.getAll(artPack.art_pack_id);
      setBackgrounds(normalizeData(data));
    } catch (error) {
      console.error("Error loading backgrounds:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, setSpinner, normalizeData]);

  // Load data when art pack changes
  useEffect(() => {
    loadBackgrounds();
  }, [loadBackgrounds]);

  // Reset form and close
  const resetForm = useCallback(() => {
    setShowForm(false);
    setIsDeleteMode(false);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  // Open form for new background
  const handleAddNew = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open form for editing
  const handleEdit = useCallback((data) => {
    setFormData(data);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open delete confirmation
  const handleDelete = useCallback((data) => {
    setFormData(data);
    setShowForm(false);
    setIsDeleteMode(true);
  }, []);

  // Create new background
  const handleCreate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await backgroundAPI.create(artPack.art_pack_id, data);
      await loadBackgrounds();
      resetForm();
    } catch (error) {
      console.error("Error creating background:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, loadBackgrounds, resetForm, setSpinner]);

  // Update existing background
  const handleUpdate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await backgroundAPI.update(artPack.art_pack_id, data.id, data);
      await loadBackgrounds();
      resetForm();
    } catch (error) {
      console.error("Error updating background:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, loadBackgrounds, resetForm, setSpinner]);

  // Delete background
  const handleDeleteConfirm = useCallback(async () => {
    setSpinner(true);
    try {
      await backgroundAPI.delete(artPack.art_pack_id, formData.id);
      await loadBackgrounds();
      resetForm();
    } catch (error) {
      console.error("Error deleting background:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, formData.id, loadBackgrounds, resetForm, setSpinner]);

  // Render delete confirmation
  if (isDeleteMode && formData.id) {
    return (
      <Delete
        onDiscard={resetForm}
        handleSubmit={handleDeleteConfirm}
        formData={formData}
      />
    );
  }

  // Render form
  if (showForm) {
    const isEditMode = Boolean(formData.rank);

    return (
      <>
        <Title title="Background Colors" />
        <Description text={DESCRIPTION_TEXT} />
        <FormCard>
          <Form
            handleSubmit={isEditMode ? handleUpdate : handleCreate}
            formData={formData}
            onDiscard={resetForm}
            typeCheck={isEditMode ? 'updateItem' : 'createItem'}
            artPack={artPack}
          />
        </FormCard>
      </>
    );
  }

  // Render main list view
  return (
    <>
      <Title title="Background Colors" />
      <Description text={DESCRIPTION_TEXT} />
      {!artPack?.approved && (
        <div className="add-btn" onClick={handleAddNew}>
          Add Background Color
          <img src={plus} className="add-icon" alt="Add" />
        </div>
      )}
      <Table
        data={backgrounds}
        onEdit={handleEdit}
        onDelete={handleDelete}
        setShowForm={setShowForm}
        setFormData={setFormData}
        isAllowedToEditOrDelete={!artPack?.approved}
      />
    </>
  );
};

export default Index;
